import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublishedBlogPosts } from '../utils/api';
import { Calendar, ArrowRight, Tag, Activity } from 'lucide-react';
import Footer from '../components/Footer';
import SaveToIdeasButton from '../components/SaveToIdeasButton';
import Seo from '../components/Seo';

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const data = await getPublishedBlogPosts();
      setPosts(data);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className={isLoggedIn ? "" : "min-h-screen bg-slate-50"}>
      <Seo
        title="CLL & SLL Blog – Research, Nutrition & Living Well | MyCBC"
        description="Educational articles for people living with CLL and SLL: blood markers explained, watch and wait, nutrition and supplement research, treatment guides and living well."
        path="/blog"
      />
      {/* Header - only show for non-logged-in users */}
      {!isLoggedIn && (
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900">MyCBC</span>
              </Link>
              <nav className="flex items-center gap-4">
                <Link to="/" className="text-slate-600 hover:text-slate-900 text-sm font-medium">
                  Home
                </Link>
                <Link to="/register" className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                  Join Free
                </Link>
              </nav>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={isLoggedIn ? "py-6" : "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12"}>
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Blog</h1>
          <p className="text-lg text-slate-600">
            Insights, updates, and educational content about CLL and blood cancer management.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-8">
            {posts.map(post => (
              <article key={post.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                {post.featured_image && (
                  <Link to={`/blog/${post.slug}`}>
                    <img 
                      src={post.featured_image} 
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-48 object-cover"
                    />
                  </Link>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(post.published_at)}</span>
                    </div>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Tag className="h-4 w-4" />
                        <span>{post.tags.slice(0, 3).join(', ')}</span>
                      </div>
                    )}
                  </div>
                  <Link to={`/blog/${post.slug}`}>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2 hover:text-primary transition-colors">
                      {post.title}
                    </h2>
                  </Link>
                  <p className="text-slate-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all"
                    >
                      Read more <ArrowRight className="h-4 w-4" />
                    </Link>
                    {isLoggedIn && (
                      <SaveToIdeasButton
                        title={post.title}
                        category="Blog"
                        sourcePage="/blog"
                        sourceUrl={`/blog/${post.slug}`}
                        variant="compact"
                      />
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {!isLoggedIn && <Footer />}
    </div>
  );
};

export default BlogListPage;
